import { useEffect, useRef, useState, FormEvent } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { SignaturePad, type SignaturePadHandle } from '../components/SignaturePad';
import { DictateButton } from '../components/DictateButton';
import { PhotoAttach, type LocalPhoto } from '../components/PhotoAttach';
import {
  TICK_LISTS, resolveDailyTemplate,
  flattenItems,
  type TickKind,
} from '../data/tickListTemplates';
import { useUserOrg } from '../lib/org';
import {
  getSite,
  createTickRun,
  type Site,
  type TickResponses,
  type TickItemResult,
} from '../lib/db';
import { uploadSignature } from '../lib/storage';
import { supabase } from '../lib/supabase';
import { enqueue as enqueueOutbox, blobToDataUrl } from '../lib/outbox';
import {
  fetchWeather,
  formatWeatherSummary,
  WEATHER_CHIPS,
  type WeatherChip,
  type WeatherSnapshot,
} from '../lib/weather';

function isOnline(): boolean {
  return typeof navigator === 'undefined' ? true : navigator.onLine;
}
import { useConfirm } from '../components/ConfirmProvider';
import { LoadingIndicator } from '../components/LoadingIndicator';

const RESULTS: { value: TickItemResult; label: string }[] = [
  { value: 'pass', label: 'OK' },
  { value: 'fail', label: 'Issue' },
  { value: 'na', label: 'N/A' },
];

export function TickListRunForm() {
  const { siteId, kind } = useParams<{ siteId: string; kind: string }>();
  const nav = useNavigate();
  const { org } = useUserOrg();
  const { confirm } = useConfirm();
  const sigRef = useRef<SignaturePadHandle>(null);

  // Pre-generate the run id so we can build photo paths before insert.
  const runIdRef = useRef<string>(crypto.randomUUID());

  const [site, setSite] = useState<Site | null>(null);
  const tpl = (() => {
    if (!kind || !(kind in TICK_LISTS)) return null;
    if (kind === 'daily') return resolveDailyTemplate(site?.project_type);
    return TICK_LISTS[kind as TickKind];
  })();
  const flat = tpl ? flattenItems(tpl) : [];
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [byName, setByName] = useState('');
  const [generalNotes, setGeneralNotes] = useState('');
  const [responses, setResponses] = useState<TickResponses>({});
  // Photos held locally (Blobs) until submit. Keyed by item key.
  const [photos, setPhotos] = useState<Record<string, LocalPhoto[]>>({});

  // Completion state — when set, we swap the form for a success screen
  // showing date / completed by / site / pass-vs-issue counts. Gives a
  // clear hand-off instead of dropping the user back at the menu
  // without confirmation. The buttons let them go back to the checks
  // list or straight home.
  const [done, setDone] = useState<null | {
    when: Date;
    byName: string;
    siteName: string;
    pass: number;
    issue: number;
    naCount: number;
    total: number;
    offline: boolean;
  }>(null);

  // Weather chip state — auto-populated from Open-Meteo when the site
  // loads (using the site's geofence centre as the lookup location).
  // User can untick/tick to override. Saved to the tick-run as part of
  // the notes string so HSE/insurance get a per-day weather record.
  const [weatherChips, setWeatherChips] = useState<WeatherChip[]>([]);
  const [weatherSnapshot, setWeatherSnapshot] = useState<WeatherSnapshot | null>(null);
  const [weatherLoading, setWeatherLoading] = useState(false);

  // Fetch weather as soon as we know the site's location. Tolerates
  // failures silently — weather is auxiliary, the tick form still
  // saves without it.
  useEffect(() => {
    if (!site || site.centre_lat == null || site.centre_lng == null) return;
    setWeatherLoading(true);
    fetchWeather({ lat: site.centre_lat, lng: site.centre_lng })
      .then((snapshot) => {
        if (snapshot) {
          setWeatherSnapshot(snapshot);
          setWeatherChips(snapshot.chips);
        }
      })
      .finally(() => setWeatherLoading(false));
  }, [site]);

  function toggleWeatherChip(key: WeatherChip) {
    setWeatherChips((p) => p.includes(key) ? p.filter((k) => k !== key) : [...p, key]);
  }

  function loadSite() {
    if (!siteId) return;
    setLoading(true);
    setError(null);
    getSite(siteId)
      .then((s) => setSite(s))
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }

  useEffect(loadSite, [siteId]);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      const name = (data.user?.user_metadata?.full_name as string | undefined) ?? '';
      if (name && !byName) setByName(name);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function setItem(key: string, state: TickItemResult) {
    setResponses((prev) => ({
      ...prev,
      [key]: { ...prev[key], state },
    }));
    if (state !== 'fail' && photos[key]?.length) {
      // Clear photos when leaving the issue state — the user can re-add if they go back.
      photos[key].forEach((p) => URL.revokeObjectURL(p.preview));
      setPhotos((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  }

  function setItemNote(key: string, notes: string) {
    setResponses((prev) => ({
      ...prev,
      [key]: { ...(prev[key] ?? { state: 'fail' }), notes },
    }));
  }

  function appendItemNote(key: string, suffix: string) {
    setResponses((prev) => {
      const cur = prev[key]?.notes ?? '';
      const sep = cur && !cur.endsWith(' ') ? ' ' : '';
      return {
        ...prev,
        [key]: {
          ...(prev[key] ?? { state: 'fail' }),
          notes: cur + sep + suffix,
        },
      };
    });
  }

  function setItemPhotos(key: string, next: LocalPhoto[]) {
    setPhotos((prev) => ({ ...prev, [key]: next }));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!org || !site || !tpl) return;
    setError(null);

    if (!byName.trim()) {
      setError('Add who carried out this check.');
      return;
    }

    const total = flat.length;
    const answered = Object.values(responses).filter((r) => r?.state).length;
    if (answered < total) {
      const ok = await confirm({
        title: 'Save with incomplete answers?',
        body: `Only ${answered} of ${total} items answered. You can come back and finish later, or save now and leave the rest blank.`,
        confirmLabel: 'Save anyway',
      });
      if (!ok) return;
    }

    setSubmitting(true);
    try {
      const runId = runIdRef.current;

      // Compose the notes string with the weather summary prefixed.
      // We don't have a dedicated weather column on tick_list_runs yet,
      // so we pack it into the notes as a tagged line that the PDF
      // generator can pick out. Format: `[Weather: chip1, chip2 (12°C
      // · 18 mph wind · 0.4 mm rain)]\n<user notes>`
      const weatherLine = weatherChips.length > 0
        ? `[Weather: ${formatWeatherSummary(weatherChips, weatherSnapshot)}]`
        : '';
      const userNotes = generalNotes.trim();
      const combinedNotes = [weatherLine, userNotes].filter(Boolean).join('\n') || null;

      // Offline path — convert all photos to base64 data URLs (keyed by
      // response key) and queue the whole run for replay.
      if (!isOnline()) {
        const photoDataUrls: Record<string, string[]> = {};
        for (const [key, list] of Object.entries(photos)) {
          if (!list?.length) continue;
          const urls: string[] = [];
          for (const ph of list) urls.push(await blobToDataUrl(ph.blob));
          photoDataUrls[key] = urls;
        }
        await enqueueOutbox('tick_run.create', org.org_id, {
          runId,
          orgId: org.org_id,
          siteId: site.id,
          kind: tpl.kind,
          performed_by_name: byName.trim(),
          responses,
          notes: combinedNotes,
          photo_data_urls: photoDataUrls,
        });
        // The tick-run signature is captured offline but not currently queued
        // (no DB column for it on tick_list_runs — it's a side-effect upload).
        // Skipping it offline is acceptable; the run itself is the audit trail.
        setDone({
          when: new Date(),
          byName: byName.trim() || 'unknown',
          siteName: site.name,
          pass: Object.values(responses).filter((r) => r?.state === 'pass').length,
          issue: Object.values(responses).filter((r) => r?.state === 'fail').length,
          naCount: Object.values(responses).filter((r) => r?.state === 'na').length,
          total: flat.length,
          offline: true,
        });
        setSubmitting(false);
        return;
      }

      // Online path — upload photos, then insert the run, then signature.
      const photoEntries: { key: string; paths: string[] }[] = [];
      for (const [key, list] of Object.entries(photos)) {
        if (!list?.length) continue;
        const paths: string[] = [];
        for (let i = 0; i < list.length; i++) {
          const p = `${org.org_id}/tick-${runId}/${key}/${i}.jpg`;
          const { error: upErr } = await supabase.storage
            .from('accident-photos')
            .upload(p, list[i].blob, { contentType: 'image/jpeg', upsert: true });
          if (upErr) throw upErr;
          paths.push(p);
        }
        photoEntries.push({ key, paths });
      }

      const finalResponses: TickResponses = { ...responses };
      for (const { key, paths } of photoEntries) {
        finalResponses[key] = { ...(finalResponses[key] ?? { state: 'fail' }), photo_paths: paths };
      }

      const sigBlob = sigRef.current?.isEmpty()
        ? null
        : await sigRef.current!.toBlob();

      await createTickRun({
        id: runId,
        orgId: org.org_id,
        siteId: site.id,
        kind: tpl.kind,
        performed_by_name: byName.trim(),
        responses: finalResponses,
        notes: combinedNotes,
      });

      if (sigBlob) {
        await uploadSignature({
          orgId: org.org_id,
          signInId: `tick-${runId}`,
          side: 'in',
          blob: sigBlob,
        });
      }

      setDone({
        when: new Date(),
        byName: byName.trim() || 'unknown',
        siteName: site.name,
        pass: Object.values(finalResponses).filter((r) => r?.state === 'pass').length,
        issue: Object.values(finalResponses).filter((r) => r?.state === 'fail').length,
        naCount: Object.values(finalResponses).filter((r) => r?.state === 'na').length,
        total: flat.length,
        offline: false,
      });
      setSubmitting(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <AppShell title="Check" back={`/sites/${siteId ?? ''}/checks`}>
        <LoadingIndicator />
      </AppShell>
    );
  }

  if (!site || !tpl) {
    return (
      <AppShell title="Check" back={`/sites/${siteId ?? ''}/checks`}>
        <div className="banner banner-error">
          {error ?? 'Tick list not found.'}
        </div>
        {!tpl ? null : (
          <button type="button" className="btn btn-ghost" onClick={loadSite} style={{ marginTop: 12 }}>
            Try again
          </button>
        )}
      </AppShell>
    );
  }

  const total = flat.length;
  const answered = Object.values(responses).filter((r) => r?.state).length;
  const passCount = Object.values(responses).filter((r) => r?.state === 'pass').length;
  const issueCount = Object.values(responses).filter((r) => r?.state === 'fail').length;
  const percent = total > 0 ? Math.round((answered / total) * 100) : 0;

  // Completion screen — renders instead of the form once the submit
  // succeeds. Gives the user a clear "saved" moment with the summary
  // they need to feel confident (date, who, site, pass/issue counts).
  if (done) {
    const dt = done.when.toLocaleString('en-GB', {
      weekday: 'short', day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
    return (
      <AppShell title={tpl.title} back={`/sites/${site.id}/checks`}>
        <div
          style={{
            margin: '0 0 16px',
            padding: '20px 18px',
            background: 'rgba(22, 163, 74, 0.10)',
            border: '1px solid var(--success, #16A34A)',
            borderRadius: 12,
            textAlign: 'center',
          }}
        >
          <p style={{ fontSize: 32, margin: '0 0 6px', lineHeight: 1 }} aria-hidden>✅</p>
          <h2 style={{ margin: 0, fontSize: 20, color: 'var(--success, #16A34A)' }}>
            {tpl.title} complete
          </h2>
          {done.offline && (
            <p className="muted" style={{ margin: '6px 0 0', fontSize: 13 }}>
              Saved offline — will sync when you're back online.
            </p>
          )}
        </div>

        <div className="card" style={{ marginBottom: 16 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <tbody>
              <tr>
                <td style={{ padding: '6px 0', color: 'var(--ink-soft)', width: '40%' }}>When</td>
                <td style={{ padding: '6px 0', fontWeight: 600 }}>{dt}</td>
              </tr>
              <tr>
                <td style={{ padding: '6px 0', color: 'var(--ink-soft)' }}>By</td>
                <td style={{ padding: '6px 0', fontWeight: 600 }}>{done.byName}</td>
              </tr>
              <tr>
                <td style={{ padding: '6px 0', color: 'var(--ink-soft)' }}>Site</td>
                <td style={{ padding: '6px 0', fontWeight: 600 }}>{done.siteName}</td>
              </tr>
              <tr>
                <td style={{ padding: '6px 0', color: 'var(--ink-soft)' }}>Result</td>
                <td style={{ padding: '6px 0', fontWeight: 600 }}>
                  {done.pass} OK · {done.issue} {done.issue === 1 ? 'issue' : 'issues'}
                  {done.naCount > 0 && ` · ${done.naCount} N/A`}
                  {' '}<span className="muted" style={{ fontWeight: 400 }}>(of {done.total})</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="btn-stack">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => nav(`/sites/${site.id}/checks`)}
          >
            Back to checks
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => nav('/')}
          >
            Home
          </button>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell title={tpl.title} back={`/sites/${site.id}/checks`}>
      <div className="card" style={{ marginBottom: 12 }}>
        <p style={{ margin: 0 }}>{tpl.subtitle}</p>
      </div>

      {/* Progress bar — sits below the intro so it's always visible at the
          top of the form. Tells the site manager how far through they are
          without scrolling. Counts every OK / Issue / N/A as "answered". */}
      <div style={{ margin: '0 0 12px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
          <span style={{ fontWeight: 600 }}>{answered} / {total} complete</span>
          {answered > 0 && (
            <span className="muted" style={{ fontSize: 13 }}>
              {passCount} OK{issueCount > 0 && ` · ${issueCount} ${issueCount === 1 ? 'issue' : 'issues'}`}
            </span>
          )}
        </div>
        <div
          role="progressbar"
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
          style={{
            height: 6,
            background: 'var(--brand-pale)',
            borderRadius: 999,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${percent}%`,
              height: '100%',
              background: 'var(--success, #16A34A)',
              transition: 'width 0.2s ease',
            }}
          />
        </div>
      </div>

      {error && <div className="banner banner-error">{error}</div>}

      <form onSubmit={onSubmit}>
        <div className="field">
          <label htmlFor="f-by">Carried out by *</label>
          <div className="input-with-mic">
            <input
              id="f-by"
              type="text"
              value={byName}
              onChange={(e) => setByName(e.target.value)}
              required
            />
            <DictateButton onTranscript={(t) => setByName((p) => (p ? p + ' ' + t : t))} />
          </div>
        </div>

        {/*
          Weather chips — auto-populated from Open-Meteo using the site's
          geofence centre. User can override with a tap. Saved into the
          notes string so it ends up on the PDF for HSE/insurance.
        */}
        <div className="field">
          <label>
            Weather on site
            {weatherLoading && (
              <span className="muted" style={{ fontSize: 12, marginLeft: 8 }}>
                checking…
              </span>
            )}
            {weatherSnapshot && (
              <span className="muted" style={{ fontSize: 12, marginLeft: 8 }}>
                {weatherSnapshot.temperatureC != null && `${Math.round(weatherSnapshot.temperatureC)}°C`}
                {weatherSnapshot.windMph != null && ` · ${Math.round(weatherSnapshot.windMph)} mph wind`}
              </span>
            )}
          </label>
          <p className="muted" style={{ margin: '0 0 8px', fontSize: 13 }}>
            Tap to adjust. Used as evidence if anything goes wrong on site.
          </p>
          <div className="rams-trade-chips" role="group" aria-label="Weather on site">
            {WEATHER_CHIPS.map((c) => (
              <button
                key={c.key}
                type="button"
                className={`rams-trade-chip${weatherChips.includes(c.key) ? ' active' : ''}`}
                onClick={() => toggleWeatherChip(c.key)}
                aria-pressed={weatherChips.includes(c.key)}
              >
                <span aria-hidden style={{ marginRight: 6 }}>{c.icon}</span>
                {c.label}
              </button>
            ))}
          </div>
          {(!site || site.centre_lat == null) && (
            <p className="muted" style={{ margin: '8px 0 0', fontSize: 12 }}>
              ⓘ Set this site's geofence (postcode in Sites) to enable auto-fill.
            </p>
          )}
        </div>

        <div className="field">
          <label htmlFor="f-notes">Notes (optional)</label>
          <div className="input-with-mic">
            <textarea
              id="f-notes"
              rows={2}
              placeholder="Anything else worth recording…"
              value={generalNotes}
              onChange={(e) => setGeneralNotes(e.target.value)}
            />
            <DictateButton onTranscript={(t) => setGeneralNotes((p) => (p ? p + ' ' + t : t))} />
          </div>
        </div>

        {tpl.sections.map((sec, si) => (
          <div key={si} style={{ marginTop: 18 }}>
            {sec.name && <h3 className="check-section-h">{sec.name}</h3>}
            <ol className="tick-list">
              {sec.items.map((item, ii) => {
                const key = `s${si}_i${ii}`;
                const r = responses[key];
                const state = r?.state;
                return (
                  <li key={key} className={`tick-row ${state === 'fail' ? 'has-issue' : ''}`}>
                    <div className="tick-row-main">
                      <span className="tick-num">{flat.findIndex((f) => f.key === key) + 1}</span>
                      <div className="tick-text">
                        <strong>{item.text}</strong>
                        {item.desc && <p className="tick-desc">{item.desc}</p>}
                      </div>
                    </div>
                    <div className="tri">
                      {RESULTS.map((opt) => (
                        <button
                          type="button"
                          key={opt.value}
                          className={`tri-btn tri-${opt.value} ${state === opt.value ? 'active' : ''}`}
                          onClick={() => setItem(key, opt.value)}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                    {state === 'fail' && (
                      <>
                        <p className="muted" style={{ flex: '1 1 100%', margin: '6px 0 0', fontSize: 12, color: 'var(--danger)' }}>
                          What's the issue and what's being done? A photo helps too.
                        </p>
                        <div className="input-with-mic" style={{ flex: '1 1 100%', marginTop: 4 }}>
                          <input
                            type="text"
                            className="issue-note"
                            placeholder="e.g. Cracked toe-board on north scaffold — replacing today"
                            value={r?.notes ?? ''}
                            onChange={(e) => setItemNote(key, e.target.value)}
                          />
                          <DictateButton onTranscript={(t) => appendItemNote(key, t)} />
                        </div>
                        <div style={{ flex: '1 1 100%' }}>
                          <PhotoAttach
                            photos={photos[key] ?? []}
                            onChange={(next) => setItemPhotos(key, next)}
                          />
                        </div>
                      </>
                    )}
                  </li>
                );
              })}
            </ol>
          </div>
        ))}

        {tpl.tail && (
          <div className="card" style={{ marginTop: 16, background: 'var(--brand-pale)', borderColor: 'var(--brand-light)' }}>
            <p style={{ margin: 0, fontWeight: 600 }}>{tpl.tail}</p>
          </div>
        )}

        <div className="card" style={{ marginTop: 16 }}>
          <p style={{ margin: 0, color: 'var(--ink-soft)', fontSize: 13 }}>
            Progress: {answered} / {total} answered · {passCount} OK
            {issueCount > 0 && ` · ${issueCount} issue(s)`}
          </p>
        </div>

        <h3 className="section-title" style={{ marginTop: 22 }}>Signature (optional)</h3>
        <SignaturePad ref={sigRef} label="Sign to confirm walk completed" />

        <div className="btn-stack">
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save check'}
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => nav(`/sites/${site.id}/checks`)}
          >
            Cancel
          </button>
        </div>
      </form>
    </AppShell>
  );
}
